/**
 * Die Zählerliste im Reiter *Energie* (Spec 46a, „Reiter Energie · unterhalb
 * der Preiskapsel"): Haus-Filter (nur die Chipform — am Schreibtisch sitzt er
 * in der Sidebar), Zeilen und die drei Zustände. Rein darstellend; die Abfrage
 * besitzt `routes/Energie.tsx`.
 *
 * ↑/↓ wandern durch die Zeilen, ↵ öffnet das Detail (Link). Die Mengen
 * (`items`, `houses`, `cancellable`) sind die des Servers; der Client filtert
 * nichts nach. Über der Liste steht „Demnächst kündbar" (Spec 46c), nur mit
 * Einträgen.
 */

import { Link } from '@tanstack/react-router'
import type { KeyboardEvent } from 'react'

import { EmptyState } from '@/components/states/EmptyState'
import { ErrorState } from '@/components/states/ErrorState'
import { SkeletonRows } from '@/components/states/Skeleton'
import type { CancellableContract, MeterHouse, MeterRow as MeterRowData } from '@/features/energy/api'
import { CancellableList } from '@/features/energy/CancellableList'
import { HouseFilter } from '@/features/energy/HouseFilter'
import { MeterRow } from '@/features/energy/MeterRow'

export type MeterListProps =
  | { readonly state: 'loading'; readonly showsChips: boolean }
  | { readonly state: 'error'; readonly error: unknown; readonly onRetry: () => void }
  | {
      readonly state: 'ready'
      readonly items: readonly MeterRowData[]
      readonly houses: readonly MeterHouse[]
      /** „Demnächst kündbar" (Spec 46c); fehlt oder leer: kein Block. */
      readonly cancellable?: readonly CancellableContract[]
      /** `haus` aus der URL (nach dem Rückfall auf „Alle"). */
      readonly selectedHouse: string | undefined
      readonly selectedMeter: string | undefined
      /** Chipzeile statt Sidebar (unter 768 px). */
      readonly showsChips: boolean
      readonly onAdd: () => void
    }

const ACTION_CLASS =
  'border-border text-text hover:bg-bg focus-visible:border-accent rounded-card border px-4 transition-colors duration-(--duration-fast) ease-(--ease-out)'

/** Pfeiltasten bewegen den Fokus zwischen den Zeilen derselben Liste. */
function moveFocus(event: KeyboardEvent<HTMLUListElement>): void {
  if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
  const rows = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('a'))
  const at = rows.indexOf(document.activeElement as HTMLElement)
  const next = rows[at + (event.key === 'ArrowDown' ? 1 : -1)]
  if (next === undefined) return
  event.preventDefault()
  next.focus()
}

export function MeterList(props: MeterListProps) {
  if (props.state === 'loading') {
    return (
      <div>
        {props.showsChips && (
          <div className="border-border flex gap-2 border-b px-(--page-x) py-2" aria-hidden="true">
            <div className="bg-border h-(--tap-min) w-20 animate-pulse rounded-card" />
          </div>
        )}
        <SkeletonRows testId="energy-meters-loading" rows={3} />
      </div>
    )
  }

  if (props.state === 'error') {
    return (
      <ErrorState
        testId="energy-meters-error"
        retryTestId="energy-meters-retry"
        error={props.error}
        onRetry={props.onRetry}
      />
    )
  }

  const { items, houses, cancellable = [], selectedHouse, selectedMeter, showsChips, onAdd } = props

  return (
    <div>
      <CancellableList items={cancellable} house={selectedHouse} />
      {showsChips && houses.length > 0 && <HouseFilter houses={houses} selected={selectedHouse} form="chips" />}

      {items.length === 0 && selectedHouse === undefined && (
        <EmptyState
          testId="energie-empty"
          title="Noch keine Zähler."
          hint="Zähler anlegen."
          action={
            <button type="button" data-testid="energy-meter-add-empty" onClick={onAdd} className={`${ACTION_CLASS} min-h-(--tap-min)`}>
              Zähler anlegen
            </button>
          }
        />
      )}

      {items.length === 0 && selectedHouse !== undefined && (
        <EmptyState
          testId="energy-meters-empty"
          title="Kein Zähler in diesem Haus."
          action={
            <Link
              to="/energie"
              search={{ haus: undefined, zaehler: undefined }}
              data-testid="energy-house-filter-reset"
              className={`${ACTION_CLASS} flex min-h-(--tap-min) items-center`}
            >
              Alle Zähler
            </Link>
          }
        />
      )}

      {items.length > 0 && (
        // `onKeyDown` am Container: bündelt die Pfeiltasten der Zeilen-Links,
        // selbst kein Bedienelement.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- Ereignisse der enthaltenen Links
        <ul onKeyDown={moveFocus}>
          {items.map((meter) => (
            <MeterRow key={meter.id} meter={meter} house={selectedHouse} isSelected={meter.id === selectedMeter} />
          ))}
        </ul>
      )}
    </div>
  )
}
